import { Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { dateToDb, dbToDate, dbToInt, EntitetStatus, isValidDate } from "@dffedb/util";
import { InstallationHttpClient, ParametreForAktuelleInstallation } from "@e-forsyning/common/http";

import { DbNote } from "../model/db-note";
import { DbNoteBilag } from "../model/db-note-bilag";
import { Note, NoteOpgavestyringStatus } from "../model/note.model";
import { NoteHenvendelsestype } from "../model/note.model";

@Injectable({ providedIn: "root" })
export class NoteDbMapperService {
    constructor(private readonly http: InstallationHttpClient) {}

    public dbToHenvendelsestyper(data: any[]): NoteHenvendelsestype[] {
        const result = data.map((h: any) => this.dbToHenvendelsestype(h));
        return result;
    }

    public dbToHenvendelsestype(data: any): NoteHenvendelsestype {
        const result: NoteHenvendelsestype = {
            key: dbToInt(data.henvendelsestypeid),
            omraadeId: dbToInt(data.henvendelsestypeomraade),
            value: data.henvendelsestypebeskriv
        };
        return result;
    }

    public dbToOpgavestyringStatusser(data: any): NoteOpgavestyringStatus[] {
        const result = data.map((s: any) => this.dbToOpgavestyringStatus(s));
        return result;
    }

    public dbToOpgavestyringStatus(data: any): NoteOpgavestyringStatus {
        const result: NoteOpgavestyringStatus = {
            key: dbToInt(data.tilstandid),
            value: data.tilstandtekst
        };
        return result;
    }

    public getBilagUrlTemplate(): Observable<string> {
        return this.http.getUrl("api/notebilag/dokumentid/[dokumentid]/omraade/[omraadeid]", ParametreForAktuelleInstallation.true);
    }

    public dbToNoter(dbNoter: DbNote[], bilagUrlTemplate: string): Note[] {
        const result = dbNoter.map((dbNote) => this.dbToNote(dbNote, bilagUrlTemplate));
        return result;
    }

    public dbToNote(dbNote: DbNote, bilagUrlTemplate: string): Note {
        const fullyLoaded =
            dbNote.hasOwnProperty("henvendelsestype") || !!(dbNote.opgavetype && parseInt(dbNote.opgavetype, 10));
        const { opfoelgDato, opfoelgTidspunkt } = this.parseOpfoelgning(dbNote);
        const genererUrl = this.genererUrl;
        const result: Note = {
            get antalBilag(): number {
                return this.bilag.length;
            },
            get erOpgave(): boolean {
                return !!this.opgaveType;
            },
            bilag: dbNote.notebilag.map((b) => ({
                filNavn: b.filnavn,
                filStoerrelseKb: dbToInt(b.filstoerelse),
                noteBilagId: dbToInt(b.dokumentid),
                status: this.beregnStatus(b.status),
                get url(): string {
                    return genererUrl(bilagUrlTemplate, dbNote, b);
                }
            })),
            dato: dbToDate(dbNote.dato),
            fullyLoaded, // !!dbNote.henvendelsestype,
            henvendelseTypeId: dbToInt(dbNote.henvendelsestype),
            noteId: dbToInt(dbNote.id),
            omraadeId: dbToInt(dbNote.omraade),
            opgavestyring: {
                opfoelgAf: dbNote.opfoelger,
                opfoelgDato,
                opfoelgTidspunkt,
                statusId: dbToInt(dbNote.tilstand),
                udfoertAf: dbNote.udfoertaf,
                udfoertDato: dbToDate(dbNote.udfoertdato)
            },
            overskrift: dbNote.overskrift,
            status: this.beregnStatus(dbNote.status),
            tekst: dbNote.notetekst.replace(/\ <br> /g, "\n"), // FOF sender <br>-tags til os. Vi ønsker linebreaks.
            opgaveType: this.readOpgaveType(dbNote),
            opgaveKontakt: dbNote.opgavekontakt || "",
            opgaveInfo: dbNote.opgaveinfo || ""
        };
        return result;
    }

    public noterToDb(noter: Note[]): DbNote[] {
        const result = noter.map((note) => this.noteToDb(note));
        return result;
    }

    public noteToDb(note: Note): DbNote {
        const result: DbNote = {
            dato: dateToDb(note.dato),
            // Opgavenotes kræver en henvendelsestype; bruges 3 som default hvis ingen er valgt.
            henvendelsestype: (note.henvendelseTypeId && note.henvendelseTypeId.toString()) || (note.opgaveType ? "3" : null),
            id: note.noteId.toString(),
            notebilag: note.bilag.map((b) => ({
                filnavn: b.filNavn,
                filstoerelse: b.filStoerrelseKb && b.filStoerrelseKb.toString(),
                dokumentid: b.noteBilagId && b.noteBilagId.toString(),
                status: b.status,
                fil: b.url
            })),
            // Serveren kræver non-empty notetekst. For opgavenotes bruges tekst hvis den er udfyldt;
            // ellers falder vi tilbage på opgaveInfo (tilfældet ved opret-opgave hvor tekst altid er "").
            notetekst: note.opgaveType ? (note.tekst || note.opgaveInfo || "") : note.tekst,
            omraade: note.omraadeId && note.omraadeId.toString(),
            opfoelgdato: dateToDb(note.opgavestyring.opfoelgDato),
            opfoelger: note.opgavestyring.opfoelgAf,
            opfoelgtidspunkt: note.opgavestyring.opfoelgTidspunkt || "",
            overskrift: note.overskrift,
            status: note.status,
            tilstand: (note.opgavestyring.statusId && note.opgavestyring.statusId.toString()) || "",
            udfoertaf: note.opgavestyring.udfoertAf,
            udfoertdato: dateToDb(note.opgavestyring.udfoertDato),
            opgavetype: (note.opgaveType && note.opgaveType.toString()) || null,
            opgavekontakt: note.opgaveKontakt || "",
            opgaveinfo: note.opgaveInfo || ""
        };
        return result;
    }

    private beregnStatus(status: EntitetStatus): EntitetStatus {
        if (status === null || status === undefined) {
            return EntitetStatus.Uaendret;
        }
        return status;
    }

    private readOpgaveType(dbNote: DbNote): number | null {
        const record = dbNote as unknown as Record<string, unknown>;
        for (const key of ["opgavetype", "Opgavetype", "opgaveType"]) {
            const value = record[key];
            if (value !== undefined && value !== null && value !== "" && value !== "0") {
                const parsed = dbToInt(value);
                if (parsed) {
                    return parsed;
                }
            }
        }
        return null;
    }

    private parseOpfoelgning(dbNote: DbNote): { opfoelgDato: Date | null; opfoelgTidspunkt: string } {
        const record = dbNote as unknown as Record<string, unknown>;
        const raw =
            dbNote.opfoelgdato ||
            (record["opfoelgningsdato"] as string) ||
            (record["Opfoelgningsdato"] as string) ||
            (record["OpfoelgningsDato"] as string) ||
            "";
        let tid = (dbNote.opfoelgtidspunkt || (record["opfoelgtid"] as string) || "").trim();

        const trimmed = (raw || "").trim();
        if (!trimmed) {
            return { opfoelgDato: null, opfoelgTidspunkt: tid };
        }

        const datetimeMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}:\d{2}(?::\d{2})?))?$/);
        if (datetimeMatch) {
            const [, day, month, year, timePart] = datetimeMatch;
            const parsed = new Date(Number(year), Number(month) - 1, Number(day));
            if (timePart && !tid) {
                tid = timePart.substring(0, 5);
            }
            return {
                opfoelgDato: isValidDate(parsed) ? parsed : null,
                opfoelgTidspunkt: tid
            };
        }

        return {
            opfoelgDato: dbToDate(trimmed),
            opfoelgTidspunkt: tid
        };
    }

    private genererUrl(bilagTemplateUrl: string, dbNote: DbNote, dbBilag: DbNoteBilag): string {
        if (dbBilag.status === EntitetStatus.Tilfoejet) {
            return dbBilag.fil;
        }

        const omraadeId = parseInt(dbNote.omraade, 10);
        const dokumentId = parseInt(dbBilag.dokumentid, 10);
        return bilagTemplateUrl.replace("[dokumentid]", dokumentId.toString()).replace("[omraadeid]", omraadeId.toString());
    }
}
